import { ErrorLike } from '@apollo/client'
import { FC, JSX, useEffect } from 'react'

import { CollectionLayoutFragmentFragment } from '../../../../__generated__/graphql/catalog/graphql'
import { ContentfulPageCollectionViewHeroSection } from '../../../../app/_components/pages/contentful/sections/ContentfulPageCollectionViewHeroSection.client'
import { ContentfulPageTabSection } from '../../../../app/_components/pages/contentful/sections/ContentfulPageTabSection.client'
import { ContentfulPageVideoSection } from '../../../../app/_components/pages/contentful/sections/ContentfulPageVideoSection.client'
import { NotFoundPage } from '../../../../app/_components/pages/NotFound.client'
import { TrackingLocationProvider } from '../../../../app/_providers/TrackingLocationProvider.client'
import { useTrackPageView } from '../../../../app/_providers/TrackingProvider.client'
import { useTranslation } from '../../../../app/_providers/TranslationProvider.client'
import {
  NostoProductRecommendationsSection,
  RecommendationWrapper,
} from '../../NostoProductRecommendationsSection'

import { CollectionPageSkeleton } from './CollectionPageSkeleton'
import { CollectionViewThemeProvider } from './CollectionViewTheme.client'
import { CollectionGridSection } from './sections/CollectionGridSection'
import { GenderNavSection } from './sections/GenderNavSection'
import { HeroFullBleedSection } from './sections/HeroFullBleedSection'
import { VideoModalBannerSection } from './sections/VideoModalBannerSection'
import { SeoContent } from './SeoContent'
import { CollectionPageTabSectionWrapper } from './styles'

import type { CollectionViewSection } from '../../../../types/graphql/collection-view'

export interface CollectionViewProps {
  collectionView?: Pick<
    CollectionLayoutFragmentFragment,
    'name' | 'sections' | 'seoContent' | 'handle' | 'theme'
  >
  error: ErrorLike | undefined
  loading: boolean
}

export const CollectionView: FC<CollectionViewProps> = ({ collectionView, error, loading }) => {
  const didQueryError = !!error

  const translations = useTranslation()
  // Note: the original collection name looks like this: "Name | FIGS"

  const { trackPageView } = useTrackPageView()

  useEffect(() => {
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (collectionView?.handle) {
      const collectionNameBeforePipeline = collectionView.name.split(' | ')[0]
      trackPageView({
        pageViewHandle: collectionView.handle,
        pageViewName: collectionNameBeforePipeline ?? collectionView.name,
        pageViewCategory: 'collection',
        iterable: {
          // Note that these Iterable properties are used by external teams at FIGS. If there are changes that alter the data getting passed through,
          // please communicate it with relevant stakeholders.
          type: 'trackCollectionView',
          payload: {
            collectionViewHandle: collectionView.handle,
          },
        },
      })
    }
  }, [collectionView?.handle, collectionView?.name, trackPageView])

  if (loading) {
    return <CollectionPageSkeleton />
  }

  if (!collectionView || didQueryError) {
    return <NotFoundPage />
  }

  return (
    <CollectionViewThemeProvider theme={collectionView.theme}>
      <main>
        {collectionView.sections.map((props, index) => (
          <TrackingLocationProvider key={index} sectionNumber={index + 1}>
            {renderSection(props, index, collectionView.handle)}
          </TrackingLocationProvider>
        ))}

        <NostoProductRecommendationsSection
          gutterSize='default'
          fullWidth
          nostoPlacementId='productcategory-nosto-1'
          title={translations.pages.product.productRecommendations.recentlyViewedTitle}
          wrapper={<RecommendationWrapper $withTopBorder data-testid='nosto-product-recos' />}
        />

        {collectionView.seoContent && <SeoContent content={collectionView.seoContent} />}
      </main>
    </CollectionViewThemeProvider>
  )
}

const renderSection = (
  props: CollectionViewSection,
  index: number,
  viewHandle: string
): JSX.Element | null => {
  const key = index
  switch (props.__typename) {
    case 'CollectionGridSection':
      return <CollectionGridSection key={key} {...props} viewHandle={viewHandle} />
    case 'CollectionViewHeroSection':
      return (
        <ContentfulPageCollectionViewHeroSection
          key={key}
          data={{
            ...props,
            ctaLink: null,
            ctaText: null,
          }}
          pageSectionIndex={index}
        />
      )
    case 'GenderNavSection':
      return <GenderNavSection key={key} {...props} />
    case 'HeroFullBleedSection':
      return <HeroFullBleedSection key={key} {...props} isCriticalImage={index === 0} />
    case 'VideoModalBannerSection':
      return <VideoModalBannerSection key={key} {...props} />
    case 'VideoSection':
      return <ContentfulPageVideoSection key={key} data={props} pageSectionIndex={index} />
    case 'PageTabSection':
      return (
        <CollectionPageTabSectionWrapper index={index} key={key}>
          <ContentfulPageTabSection data={props} pageSectionIndex={index} />
        </CollectionPageTabSectionWrapper>
      )
    default:
      return null
  }
}
